// src/hr/WhosOutCalendar.js
// Who's out, Monday to Friday, from GET /api/hr/whos-out (shared by Time Off and the HR Portal).
// Shows names and initials only -- never a type, hours or a note -- so nobody can tell sick time from
// vacation. Company holidays are labelled and nobody is listed on them.
//   mode 'week':  this week's five days plus who is out today (My Time Off)
//   mode 'month': a month grid with previous / next, and the names for the picked day
//   scope 'team': only the signed-in manager's direct reports (Time Off -> My team); no scope = everyone
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiSun, FiAlertCircle } from 'react-icons/fi';
import { hr } from './api';
import { Avatar, Btn, Skeleton } from './kit';
import { plainId } from './kit/util';
import {
  todayNY, addDays, monthOf, firstOfMonth, lastOfMonth, shiftMonth, monthTitle, monthWeeks,
  weekdayShort, shortDate, longDate, dayOfMonth
} from './timeoff/dates';

const WEEKDAY_HEADS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// Loads whos-out for from..to (no dates = this week on the server), for everyone or for `scope`.
// Late answers for an older range, or after unmount, are dropped.
function useWhosOut(from, to, scope) {
  const [state, setState] = useState({ status: 'loading', days: [] });
  const latest = useRef(0);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    latest.current += 1;
    const call = latest.current;
    let live = true;
    setState({ status: 'loading', days: [] });
    let request;
    try {
      if (scope) request = hr.whosOut(from || undefined, to || undefined, { scope });
      else request = from ? hr.whosOut(from, to) : hr.whosOut();
    } catch (err) {
      request = Promise.reject(err);
    }
    Promise.resolve(request)
      .then((body) => {
        if (live && call === latest.current) setState({ status: 'ready', days: (body && body.days) || [] });
      })
      .catch(() => {
        if (live && call === latest.current) setState({ status: 'error', days: [] });
      });
    return () => { live = false; };
  }, [from, to, scope, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

// Names only: whatever else a row carries is ignored here.
const peopleOf = (day) => ((day && day.people) || []).map((p) => ({ id: p.employeeId, name: p.name }));

function namesSentence(names) {
  if (names.length === 0) return 'everyone in';
  if (names.length === 1) return `${names[0]} out`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} out`;
}

function Stack({ people, max = 3, size = 'sm' }) {
  const shown = people.slice(0, max);
  const more = people.length - shown.length;
  return (
    <span className="hrp-stk">
      {shown.map((p) => <Avatar key={p.id} id={p.id} name={p.name} size={size} />)}
      {more > 0 && <span className="hrp-stk__more">+{more}</span>}
    </span>
  );
}

function LoadError({ onRetry }) {
  return (
    <div className="hrp-wo-err" role="alert">
      <FiAlertCircle aria-hidden="true" />
      <span>Couldn't load who's out.</span>
      <Btn variant="ghost" size="sm" onClick={onRetry}>Try again</Btn>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Week strip
// ---------------------------------------------------------------------------------------------

// "Out today · back tomorrow" / "Out through Wed" for someone out today, from this week's days.
function returnNote(days, index, personId, today) {
  const outOn = (day) => !!day && (day.holiday || peopleOf(day).some((p) => p.id === personId));
  let last = index;
  while (last + 1 < days.length && outOn(days[last + 1])) last += 1;
  while (last > index && days[last].holiday) last -= 1;
  if (last > index) return `Out through ${weekdayShort(days[last].date)}`;
  const next = days[index + 1];
  if (!next) return 'Out today';
  return `Out today · back ${next.date === addDays(today, 1) ? 'tomorrow' : weekdayShort(next.date)}`;
}

function WeekView({ today, scope }) {
  const { status, days, retry } = useWhosOut(undefined, undefined, scope);

  if (status === 'error') return <LoadError onRetry={retry} />;
  if (status === 'loading') {
    return (
      <div className="hrp-wo" aria-busy="true">
        <Skeleton label="Loading who's out" className="hrp-sr" />
        <ul className="hrp-wk" aria-hidden="true">
          {WEEKDAY_HEADS.map((d) => (
            <li key={d} className="hrp-wk__d">
              <b>{d}</b>
              <span className="hrp-skel hrp-wk__skel" />
              <span className="hrp-skel hrp-skel--circle hrp-wk__skelav" />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const todayIndex = days.findIndex((d) => d.date === today);
  const outToday = todayIndex >= 0 ? peopleOf(days[todayIndex]) : [];

  return (
    <div className="hrp-wo">
      <ul className="hrp-wk" aria-label="This week">
        {days.map((day) => {
          const people = peopleOf(day);
          const isToday = day.date === today;
          return (
            <li key={day.date} className="hrp-wk__d" data-today={isToday ? 'true' : undefined} data-holiday={day.holiday ? 'true' : undefined}>
              <b>{isToday ? 'Today' : weekdayShort(day.date)}</b>
              <em>{shortDate(day.date)}</em>
              <span className="hrp-sr">{`${longDate(day.date)}: ${day.holiday ? `${day.holiday}, company holiday` : namesSentence(people.map((p) => p.name))}`}</span>
              <span className="hrp-wk__body" aria-hidden="true">
                {day.holiday && <span className="hrp-wk__hol" title={day.holiday}><FiSun /><span className="hrp-wk__holt">{day.holiday}</span></span>}
                {!day.holiday && people.length === 0 && <span className="hrp-wk__none">All in</span>}
                {!day.holiday && people.length > 0 && <Stack people={people} />}
              </span>
            </li>
          );
        })}
      </ul>
      {todayIndex >= 0 && (outToday.length === 0
        ? <p className="hrp-who__none">Everyone's in today.</p>
        : (
          <ul className="hrp-who" aria-label="Out today">
            {outToday.map((p) => (
              <li key={p.id}>
                <Avatar id={p.id} name={p.name} size="md" />
                <span className="hrp-who__name">{p.name}</span>
                <em>{returnNote(days, todayIndex, p.id, today)}</em>
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Month grid
// ---------------------------------------------------------------------------------------------

function MonthView({ today, scope }) {
  const [month, setMonth] = useState(monthOf(today));
  const [picked, setPicked] = useState(today);
  const from = firstOfMonth(month);
  const to = lastOfMonth(month);
  const { status, days, retry } = useWhosOut(from, to, scope);
  const titleId = `hrp-cal-${plainId(useId())}`;
  const cellRefs = useRef({});

  const weeks = useMemo(() => monthWeeks(month), [month]);
  const inMonth = useMemo(() => weeks.flat().filter((d) => monthOf(d) === month), [weeks, month]);
  const byDate = useMemo(() => new Map(days.map((d) => [d.date, d])), [days]);
  const selected = inMonth.includes(picked) ? picked : null;
  const focusDate = selected || (inMonth.includes(today) ? today : inMonth[0]);

  const go = (by) => {
    const next = shiftMonth(month, by);
    setMonth(next);
    setPicked(monthOf(today) === next ? today : null);
  };
  const goToday = () => {
    setMonth(monthOf(today));
    setPicked(today);
  };

  const pick = (date, focus) => {
    setPicked(date);
    if (focus && cellRefs.current[date]) cellRefs.current[date].focus();
  };

  const onKeyDown = (e, date) => {
    const flat = weeks.flat();
    const at = flat.indexOf(date);
    const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -5, ArrowDown: 5 };
    let target = null;
    if (moves[e.key] !== undefined) target = flat[at + moves[e.key]];
    else if (e.key === 'Home') target = inMonth[0];
    else if (e.key === 'End') target = inMonth[inMonth.length - 1];
    else return;
    e.preventDefault();
    if (target && monthOf(target) === month) pick(target, true);
  };

  const pickedDay = selected ? byDate.get(selected) : null;
  const pickedPeople = peopleOf(pickedDay);
  const ready = status === 'ready';

  return (
    <div className="hrp-cal">
      <div className="hrp-cal__head">
        <h2 id={titleId} className="hrp-cal__title">{monthTitle(month)}</h2>
        <div className="hrp-cal__nav">
          <Btn variant="ghost" size="sm" icon={FiChevronLeft} aria-label="Previous month" onClick={() => go(-1)} />
          <Btn variant="ghost" size="sm" disabled={month === monthOf(today)} onClick={goToday}>Today</Btn>
          <Btn variant="ghost" size="sm" icon={FiChevronRight} aria-label="Next month" onClick={() => go(1)} />
        </div>
      </div>

      {status === 'error' && <LoadError onRetry={retry} />}
      {status === 'loading' && <Skeleton label="Loading who's out" className="hrp-sr" />}

      <div className="hrp-cal__body">
        <div className="hrp-cal__grid" role="grid" aria-labelledby={titleId} aria-busy={status === 'loading' || undefined}>
          <div className="hrp-cal__row hrp-cal__row--head" role="row">
            {WEEKDAY_HEADS.map((d) => <span key={d} role="columnheader" className="hrp-cal__wd">{d}</span>)}
          </div>
          {weeks.map((week) => (
            <div className="hrp-cal__row" role="row" key={week[0]}>
              {week.map((date) => {
                if (monthOf(date) !== month) {
                  return (
                    <span key={date} role="gridcell" className="hrp-cal__cell hrp-cal__cell--out" aria-disabled="true">
                      <b className="hrp-cal__num" aria-hidden="true">{dayOfMonth(date)}</b>
                    </span>
                  );
                }
                const day = byDate.get(date);
                const people = peopleOf(day);
                const holiday = day && day.holiday;
                const label = `${longDate(date)}: ${!ready ? 'loading' : holiday ? `${holiday}, company holiday` : namesSentence(people.map((p) => p.name))}`;
                return (
                  <span key={date} role="gridcell" className="hrp-cal__gc">
                    <button
                      ref={(el) => { cellRefs.current[date] = el; }}
                      type="button"
                      className="hrp-cal__cell"
                      tabIndex={date === focusDate ? 0 : -1}
                      aria-pressed={date === selected}
                      aria-current={date === today ? 'date' : undefined}
                      aria-label={label}
                      data-holiday={holiday ? 'true' : undefined}
                      onClick={() => pick(date, false)}
                      onKeyDown={(e) => onKeyDown(e, date)}
                    >
                      <span className="hrp-cal__top" aria-hidden="true">
                        <b className="hrp-cal__num">{dayOfMonth(date)}</b>
                        {ready && people.length > 0 && <span className="hrp-cal__n">{people.length}</span>}
                      </span>
                      <span className="hrp-cal__in" aria-hidden="true">
                        {!ready && status === 'loading' && <span className="hrp-skel hrp-cal__skel" />}
                        {ready && holiday && <span className="hrp-cal__hol" title={holiday}>{holiday}</span>}
                        {ready && !holiday && people.length > 0 && <Stack people={people} />}
                      </span>
                    </button>
                  </span>
                );
              })}
            </div>
          ))}
        </div>

        <aside className="hrp-cal__day" aria-live="polite">
          {selected ? (
            <>
              <div className="hrp-lab">{selected === today ? 'Today' : 'Selected day'}</div>
              <h3 className="hrp-cal__dayt">{longDate(selected)}</h3>
              {!ready && <Skeleton lines={3} />}
              {ready && pickedDay && pickedDay.holiday && (
                <p className="hrp-cal__dayhol"><FiSun aria-hidden="true" /> Company holiday · {pickedDay.holiday}</p>
              )}
              {ready && !(pickedDay && pickedDay.holiday) && pickedPeople.length === 0 && (
                <p className="hrp-cal__daynone">Everyone's in.</p>
              )}
              {ready && pickedPeople.length > 0 && (
                <ul className="hrp-cal__people" aria-label={`Out on ${longDate(selected)}`}>
                  {pickedPeople.map((p) => (
                    <li key={p.id}><Avatar id={p.id} name={p.name} size="md" /><span>{p.name}</span></li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <p className="hrp-cal__daynone">Pick a day to see who's out.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

export function WhosOutCalendar({ mode = 'week', today, scope }) {
  const day = today || todayNY();
  return mode === 'month' ? <MonthView today={day} scope={scope} /> : <WeekView today={day} scope={scope} />;
}

export default WhosOutCalendar;
